import { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { FolderOpen } from "@mui/icons-material";
import { useMutation } from "@apollo/client";

import { SalesAidsStyles } from "./SalesAidsStyles";
import Theme from "../../../../../utils/Theme";
import { GET_VIDEO_UPLOAD_URL } from "../../../../../graphql/mutation/mutation";
import {
  uploadVideoFileToS3,
  rejectWalkthroughEmbedUrl,
} from "../../../../../utils/videoPlayer";

const URL_FIELDS = [
  "video_url",
  "es_video_url",
  "knowledgehub_video_url",
  "es_knowledgehub_video_url",
];

const WalkthroughVideo = ({ artifacts, setArtifacts }) => {
  const [dragOver, setDragOver] = useState({});
  const [uploading, setUploading] = useState({});
  const [urlErrors, setUrlErrors] = useState({});
  const fileInputRefs = useRef({});
  const [getVideoUploadUrl] = useMutation(GET_VIDEO_UPLOAD_URL);

  // Show error for existing unsupported YouTube/Vimeo URLs when data loads
  useEffect(() => {
    const next = {};
    URL_FIELDS.forEach((field) => {
      next[field] = rejectWalkthroughEmbedUrl(artifacts?.[field]);
    });
    setUrlErrors(next);
  }, [
    artifacts?.video_url,
    artifacts?.es_video_url,
    artifacts?.knowledgehub_video_url,
    artifacts?.es_knowledgehub_video_url,
  ]);

  const handleTextChange = (field, value) => {
    if (URL_FIELDS.includes(field)) {
      setUrlErrors((prev) => ({
        ...prev,
        [field]: rejectWalkthroughEmbedUrl(value),
      }));
    }

    setArtifacts((prev) => ({
      ...prev,
      walkthrough_video: {
        ...prev.walkthrough_video,
        [field]: value,
        isEdit: true,
      },
    }));
  };

  const handleFileSelect = async (field, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      const { publicUrl } = await uploadVideoFileToS3(getVideoUploadUrl, file);
      handleTextChange(field, publicUrl);
      setUrlErrors((prev) => ({ ...prev, [field]: null }));
    } catch (err) {
      alert(err?.message || "Upload failed");
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const renderUploadZone = (field) => {
    const isOver = dragOver[field];
    const isBusy = uploading[field];

    return (
      <>
        <input
          type="file"
          accept="video/mp4"
          style={{ display: "none" }}
          ref={(el) => (fileInputRefs.current[field] = el)}
          onChange={(e) => handleFileSelect(field, e.target.files?.[0])}
        />
        <Box
          onClick={() => !isBusy && fileInputRefs.current[field]?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver((prev) => ({ ...prev, [field]: true }));
          }}
          onDragLeave={() =>
            setDragOver((prev) => ({ ...prev, [field]: false }))
          }
          onDrop={(e) => {
            e.preventDefault();
            setDragOver((prev) => ({ ...prev, [field]: false }));
            const file = e.dataTransfer.files?.[0];
            if (file) handleFileSelect(field, file);
          }}
          sx={{
            width: 110,
            minWidth: 110,
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: isBusy ? "wait" : "pointer",
            bgcolor: isOver ? `${Theme.palette.primary.main}10` : "transparent",
            border: "1px dashed rgba(0,0,0,0.2)",
            borderRadius: 1,
            gap: 0.5,
            py: 1,
          }}
        >
          {isBusy ? (
            <CircularProgress size={18} />
          ) : (
            <FolderOpen
              sx={{
                fontSize: 18,
                color: isOver ? Theme.palette.primary.main : "rgba(0,0,0,0.4)",
              }}
            />
          )}
          <Typography
            sx={{
              fontSize: 12,
              color: isOver ? Theme.palette.primary.main : "rgba(0,0,0,0.4)",
            }}
          >
            {isBusy ? "Uploading…" : "Upload"}
          </Typography>
        </Box>
      </>
    );
  };

  const renderUrlRow = (field, label) => {
    const err = urlErrors[field] || rejectWalkthroughEmbedUrl(artifacts?.[field]);

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          alignItems: "stretch",
          width: "100%",
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          label={label}
          onChange={(e) => handleTextChange(field, e.target.value)}
          value={artifacts?.[field] || ""}
          multiline
          error={!!err}
          helperText={err || "YouTube, Vimeo, or direct MP4/HLS URL"}
          FormHelperTextProps={{
            sx: err ? { color: "error.main", fontWeight: 600 } : undefined,
          }}
        />
        {renderUploadZone(field)}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: "6px",
        bgcolor: artifacts?.isEdit ? "#FFF7F7" : null,
      }}
    >
      <Typography sx={SalesAidsStyles.wordStyle}>
        Insurance Verification Workflow
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography fontSize={15} fontWeight="bold" color="gray">
            English
          </Typography>
          {renderUrlRow("video_url", "Video Url")}
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography fontSize={15} fontWeight="bold" color="gray">
            Spanish
          </Typography>
          {renderUrlRow("es_video_url", "URL del vídeo")}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          label={"Message to be displayed"}
          onChange={(e) => handleTextChange("text", e.target.value)}
          value={artifacts?.text}
          multiline
        />

        <TextField
          fullWidth
          variant="outlined"
          label={"Mensaje a mostrar"}
          onChange={(e) => handleTextChange("es_text", e.target.value)}
          value={artifacts?.es_text}
          multiline
        />
      </Box>
      <Typography sx={SalesAidsStyles.wordStyle}>
        No Insurance Verification Workflow
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography fontSize={15} fontWeight="bold" color="gray">
            English
          </Typography>
          {renderUrlRow("knowledgehub_video_url", "Video Url")}
        </Box>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography fontSize={15} fontWeight="bold" color="gray">
            Spanish
          </Typography>
          {renderUrlRow("es_knowledgehub_video_url", "URL del vídeo")}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 2,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          label={"Message to be displayed"}
          onChange={(e) =>
            handleTextChange("knowledgehub_text", e.target.value)
          }
          value={artifacts?.knowledgehub_text}
          multiline
        />

        <TextField
          fullWidth
          variant="outlined"
          label={"Mensaje a mostrar"}
          onChange={(e) =>
            handleTextChange("es_knowledgehub_text", e.target.value)
          }
          value={artifacts?.es_knowledgehub_text}
          multiline
        />
      </Box>
    </Box>
  );
};

export default WalkthroughVideo;